/**
 * 管理端会话/权限客户端上下文。
 *
 * 登录设置 enduser.id(员工 ID)/session.id(令牌哈希派生)/organizationId，退出清空。
 * 上下文为客户端状态，MUST NOT 作为权威事实，提交与响应以服务端校验为准。
 * 对齐 staff-rbac-wechat-login:SC-49/SC-50/SC-51/SC-52。
 *
 * 解耦：transport 通过 `configureTransport` 注入的 getToken/onUnauthorized 读取模块级镜像，
 * 不直接依赖 React 上下文；onSessionChange 回调供外部（OTel 用户上下文）订阅。
 */
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { configureTransport } from '../api/transport';
import { authApi, type PermissionSnapshot } from '../api/auth.api';
import { clearPersistedSession, persistSession } from './session-storage';

/** 客户端会话状态（真实令牌 + 岗位，登录后由后端返回）。 */
export interface SessionState {
  /** 员工 ID（员工编号） */
  staffId: string;
  /** 姓名 */
  name: string;
  /** 会话令牌（Transport Authorization Bearer；OTel session.id 仅上报其哈希派生值） */
  token: string;
  /** 岗位列表（用于菜单粗粒度可见性） */
  positions: string[];
  /** 机构 ID（OTel 用户上下文用，登录无则置空） */
  organizationId: string;
}

interface SessionContextValue {
  session: SessionState | null;
  /** 权限快照（登录后从 GET /auth/permissions 加载） */
  permissions: PermissionSnapshot | null;
  /** 权限加载中标记 */
  permissionsLoading: boolean;
  /** 建立会话（设置令牌到镜像） */
  login: (s: SessionState) => void;
  /** 拉取并缓存当前用户权限快照 */
  refreshPermissions: () => Promise<void>;
  /** 清空会话与权限 */
  logout: () => void;
}

const SessionContext = createContext<SessionContextValue | null>(null);

// 模块级镜像：供 Transport（非 React 上下文）读取 token 与触发登出
let mirrorSession: SessionState | null = null;

export interface SessionProviderProps {
  children: ReactNode;
  /** 401 处理：清空会话后由外部跳转登录页（注入 navigate） */
  onUnauthorized?: () => void;
  /** 会话变更订阅：供 OTel 设置/清空用户上下文（enduser.id/session.id/organizationId） */
  onSessionChange?: (s: SessionState | null) => void;
  /**
   * 初始会话（可选）：用于会话恢复场景直接以已登录态初始化，跳过首帧 null session。
   * 真实登录流程仍应经 login()（会触发 onSessionChange 设置 OTel 用户上下文）。
   */
  initialSession?: SessionState | null;
  /**
   * 初始权限快照（可选，测试专用）：跳过异步拉取直接以已授权态首帧渲染，
   * 供页面组件测试同步断言受权限控制的按钮。生产不传（默认 null 由布局层拉取）。
   */
  initialPermissions?: PermissionSnapshot | null;
}

export function SessionProvider({
  children,
  onUnauthorized,
  onSessionChange,
  initialSession = null,
  initialPermissions = null,
}: SessionProviderProps) {
  // 会话恢复场景必须在首帧渲染（useState 惰性初始化器）同步镜像：
  // React effect 执行顺序为子组件先于父组件，若延迟到 useEffect，刷新后子组件首批请求
  // （/auth/permissions、页面数据）会在镜像就绪前发出，缺失 Authorization 导致 401 误登出。
  const [session, setSession] = useState<SessionState | null>(() => {
    mirrorSession = initialSession;
    return initialSession;
  });
  const [permissions, setPermissions] = useState<PermissionSnapshot | null>(initialPermissions);
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  // 会话 ref：refreshPermissions 闭包内读取最新会话（回写岗位），避免 useCallback 依赖重建
  const sessionRef = useRef(session);
  sessionRef.current = session;

  const login = useCallback(
    (s: SessionState) => {
      mirrorSession = s;
      setSession(s);
      // 持久化会话快照：刷新后经 App 启动恢复（权限快照不持久化，每次启动重拉）
      persistSession(s);
      onSessionChange?.(s);
    },
    [onSessionChange],
  );

  const refreshPermissions = useCallback(async () => {
    setPermissionsLoading(true);
    try {
      const snapshot = await authApi.getPermissions();
      setPermissions(snapshot);
      // 回写真实岗位到会话：登录响应不含 positions（login 时写死空数组），
      // 顶栏岗位展示与 menuKeys 缺失时的岗位兼容兜底依赖此处回填；持久化同步更新。
      const prev = sessionRef.current;
      if (
        prev &&
        (prev.positions.length !== snapshot.positions.length ||
          prev.positions.some((pos, idx) => pos !== snapshot.positions[idx]))
      ) {
        const next = { ...prev, positions: snapshot.positions };
        mirrorSession = next;
        setSession(next);
        persistSession(next);
      }
    } catch {
      // 权限拉取失败不阻塞会话（菜单按粗粒度兜底，越权访问由后端 403 兜底）；
      // 快照置 null 供布局层提示「权限加载失败」并提供重试入口
      setPermissions(null);
    } finally {
      setPermissionsLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    mirrorSession = null;
    setSession(null);
    setPermissions(null);
    // 同步清除持久化会话（防止登出后刷新又恢复）
    clearPersistedSession();
    onSessionChange?.(null);
  }, [onSessionChange]);

  // 配置 Transport：getToken 从镜像读，身份 header 从镜像读（供后端 identity-context.middleware），
  // 401 触发 logout + 外部跳转。callerKind 一期恒 staff（2-2 RBAC 后按真实角色）；campusScope 一期 all（总部视角）。
  // render 期幂等注入（useMemo 同步执行）：必须早于子组件 effect 完成注册，否则刷新后首批请求
  // 会在 tokenProvider 就绪前发出（默认空实现无 Authorization），401 误登出。
  useMemo(() => {
    configureTransport({
      getToken: () => mirrorSession?.token ?? null,
      getIdentity: () =>
        mirrorSession
          ? {
              organizationId: mirrorSession.organizationId,
              callerId: mirrorSession.staffId,
              callerKind: 'staff' as const,
              campusScope: 'all',
            }
          : null,
      onUnauthorized: () => {
        logout();
        onUnauthorized?.();
      },
    });
  }, [logout, onUnauthorized]);

  return (
    <SessionContext.Provider
      value={{ session, permissions, permissionsLoading, login, refreshPermissions, logout }}
    >
      {children}
    </SessionContext.Provider>
  );
}

export function useSession(): SessionContextValue {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession 必须在 SessionProvider 内使用');
  return ctx;
}
